import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { CapabilityDomain, CapabilityDecision, CAPABILITY_DOMAIN_LABELS } from '../capabilities/types';
import { openAI } from '../../../../services/openAIService';
import { ChatIcon, AssistantIcon } from '../../../icons';

// =============================================================================
// TYPES
// =============================================================================

interface AISparkPanelProps {
  milestone: {
    title: string;
    domain: CapabilityDomain;
    actions: Array<{ title: string; enabled: boolean }>;
  };
  assessmentContext: {
    type: 'capability' | 'selftest' | 'presparring';
    decision?: CapabilityDecision;
    extendedAnswers?: Record<string, any>;
    goalContext?: string;
  };
  onAddAction: (actionTitle: string) => void;
  onUpdateAction?: (index: number, newTitle: string) => void;
  onClose: () => void;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * AISparkPanel - Universele AI chat ondersteuning voor mijlpalen
 * 
 * Features:
 * - Context-aware AI chat
 * - Quick questions
 * - Action extraction en toevoegen
 * - Markdown support
 */
const AISparkPanel: React.FC<AISparkPanelProps> = ({
  milestone,
  assessmentContext,
  onAddAction,
  onClose,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Initialize with welcome message
  useEffect(() => {
    const welcomeMessage = generateWelcomeMessage(milestone, assessmentContext);
    setMessages([{
      role: 'assistant',
      content: welcomeMessage
    }]);
  }, []);
  
  const sendMessage = async (messageText?: string) => {
    const textToSend = messageText || input;
    if (!textToSend.trim()) return;
    
    const userMessage: Message = { role: 'user', content: textToSend };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsThinking(true);
    
    try {
      // Build system prompt
      const systemPrompt = buildSparkSystemPrompt(milestone, assessmentContext);
      
      // Call AI
      const response = await openAI.generateCompletion([
        { role: 'system', content: systemPrompt },
        ...messages,
        userMessage
      ]);
      
      if (response.success && response.content) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: response.content!
        }]);
      } else {
        throw new Error(response.error || 'AI response failed');
      }
    } catch (err) {
      console.error('AI Spark error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, er ging iets mis. Probeer het opnieuw.'
      }]);
    } finally {
      setIsThinking(false);
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-blue-600">
          <div className="flex items-center gap-3">
            <ChatIcon size={28} className="text-white" />
            <div>
              <h3 className="text-lg font-semibold text-white">
                Sparren: {milestone.title}
              </h3>
              <p className="text-xs text-blue-100">
                {CAPABILITY_DOMAIN_LABELS[milestone.domain]}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-red-200 transition-colors text-2xl"
          >
            ✕
          </button>
        </div>
        
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-lg px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-orange-600 text-white'
                    : 'bg-white text-gray-800 shadow-md border border-gray-200'
                }`}
              >
                <div className="prose prose-sm max-w-none">
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                </div>
                
                {/* Extract and show action suggestions */}
                {msg.role === 'assistant' && (
                  <ActionSuggestions
                    content={msg.content}
                    onAdd={onAddAction}
                  />
                )}
              </div>
            </div>
          ))}
          
          {isThinking && (
            <div className="flex justify-start">
              <div className="bg-white rounded-lg px-4 py-3 shadow-md border border-gray-200">
                <div className="flex items-center gap-2">
                  <div className="animate-bounce"><AssistantIcon size={24} className="text-gray-500" /></div>
                  <span className="text-gray-600">Sparren...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input Area */}
        <div className="border-t bg-white p-4 space-y-3">
          {/* Quick Questions */}
          <div className="flex gap-2 flex-wrap">
            <QuickQuestion
              text="Geef me 3 extra acties"
              onClick={() => sendMessage("Kun je 3 extra specifieke acties voorstellen voor deze mijlpaal?")}
            />
            <QuickQuestion
              text="Welke tools kan ik gebruiken?"
              onClick={() => sendMessage(`Welke tools of platforms kan ik gebruiken voor ${milestone.title}?`)}
            />
            <QuickQuestion
              text="Wat zijn valkuilen?"
              onClick={() => sendMessage(`Wat zijn veelgemaakte fouten bij ${milestone.title}?`)}
            />
            <QuickQuestion
              text="Hoe begin ik?"
              onClick={() => sendMessage("Wat is de beste eerste stap om mee te beginnen?")}
            />
          </div>
          
          {/* Text Input */}
          <div className="flex gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Stel een vraag over deze mijlpaal... (Enter = verzenden, Shift+Enter = nieuwe regel)"
              rows={2}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg resize-none
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || isThinking}
              className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg
                       font-semibold disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all shadow-md hover:shadow-lg"
            >
              {isThinking ? '...' : 'Verstuur'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

const QuickQuestion: React.FC<{ text: string; onClick: () => void }> = ({ text, onClick }) => (
  <button
    onClick={onClick}
    className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-sm rounded-full
             border border-gray-300 transition-colors text-gray-700"
  >
    {text}
  </button>
);

const ActionSuggestions: React.FC<{
  content: string;
  onAdd: (action: string) => void;
}> = ({ content, onAdd }) => {
  const actions = extractActionsFromResponse(content);
  
  if (actions.length === 0) return null;
  
  return (
    <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
      <p className="text-xs font-semibold text-gray-600">Snel toevoegen:</p>
      {actions.map((action, i) => (
        <button
          key={i}
          onClick={() => {
            onAdd(action);
            // Visual feedback
            const btn = document.activeElement as HTMLButtonElement;
            if (btn) {
              const originalText = btn.innerText;
              btn.innerText = '✓ Toegevoegd!';
              btn.disabled = true;
              setTimeout(() => {
                btn.innerText = originalText;
                btn.disabled = false;
              }, 2000);
            }
          }}
          className="w-full text-left px-3 py-2 bg-orange-50 hover:bg-orange-100 
                   border border-orange-200 rounded-lg text-sm transition-all 
                   flex items-center gap-2 text-gray-700 hover:text-gray-900"
        >
          <span className="text-orange-500 font-bold">+</span>
          <span className="flex-1">{action}</span>
        </button>
      ))}
    </div>
  );
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function buildSparkSystemPrompt(
  milestone: {
    title: string;
    domain: CapabilityDomain;
    actions: Array<{ title: string; enabled: boolean }>;
  },
  assessmentContext: any
): string {
  const domainLabel = CAPABILITY_DOMAIN_LABELS[milestone.domain];
  
  return `
Je bent een ervaren business coach die helpt bij het verfijnen van actielijsten voor ondernemers.

CONTEXT:
- Mijlpaal: "${milestone.title}"
- Domein: ${domainLabel}
- Assessment type: ${assessmentContext.type}
- Huidige acties: ${milestone.actions.filter((a: any) => a.enabled).length} acties geselecteerd
- Beslissing: ${assessmentContext.decision?.resolution || 'Niet beschikbaar'}

JOUW ROL:
- Beantwoord vragen over deze specifieke mijlpaal
- Stel extra acties voor als gevraagd (prefix met "ACTIE:")
- Geef praktische tips, tools en resources
- Wees specifiek en actionable
- Gebruik Nederlands
- Houd het kort en krachtig (max 200 woorden per antwoord)

REGELS:
✅ Als je acties voorstelt, gebruik prefix "ACTIE: [actie titel]"
✅ Max 3-5 acties per keer
✅ Vermeld concrete tools, platforms, websites
✅ Geef realistische tijdsinschattingen
✅ Blijf binnen scope van deze mijlpaal en domein

❌ Vraag nooit om persoonlijke/gevoelige informatie
❌ Geen vage adviezen - altijd concrete stappen
❌ Geen lange verhalen - kort en krachtig

VOORBEELD INTERACTIE:
User: "Welke tools kan ik gebruiken voor social media?"
Assistant: "Voor social media management zijn deze tools handig:

**Planning & Scheduling:**
• Buffer - gratis tot 3 accounts
• Later - visuele Instagram planner
• Hootsuite - alles-in-één (betaald)

**Content Creatie:**
• Canva - templates voor posts
• CapCut - video editing (gratis)

ACTIE: Maak gratis Buffer account aan
ACTIE: Installeer Canva app
ACTIE: Test 3 post templates in Canva

Wil je tips voor een specifiek platform?"
`;
}

function generateWelcomeMessage(milestone: any, context: any): string {
  const enabledCount = milestone.actions.filter((a: any) => a.enabled).length;
  
  return `
👋 Hoi! Ik help je graag met je **${milestone.title}** mijlpaal.

Je hebt nu **${enabledCount} acties** geselecteerd.

**Hoe kan ik helpen?**
• Extra acties voorstellen?
• Tips over tools of aanpak?
• Valkuilen of best practices?
• Specifieke vragen beantwoorden?

Stel gerust je vraag! 💪
  `.trim();
}

function extractActionsFromResponse(text: string): string[] {
  // Extract lines with "ACTIE:" prefix
  const matches = text.match(/ACTIE:\s*(.+)/gi) || [];
  return matches
    .map(m => m.replace(/^ACTIE:\s*/i, '').trim())
    .filter(a => a.length > 5 && a.length < 150);
}

export default AISparkPanel;
